import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  ArrowLeft, Shield, ShieldCheck, Clock, AlertTriangle, FileText,
  Plus, Gavel, Download, Lock, Eye, Send, Loader2, CheckCircle, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_CONFIG = {
  protected: { color: "#556B2F", label: "Protected", icon: ShieldCheck },
  under_review: { color: "#F59E0B", label: "Under Review", icon: Clock },
  high_risk: { color: "#ef4444", label: "High Risk", icon: AlertTriangle },
  unprotected: { color: "#71717A", label: "Unprotected", icon: Shield },
};

const SEVERITY_COLORS = { low: "#556B2F", medium: "#F59E0B", high: "#ef4444", critical: "#ef4444" };
const DISPUTE_STATUS_COLORS = { filed: "#F59E0B", draft_generated: "#D4AF37", lawyer_assigned: "#556B2F", resolved: "#556B2F" };

export default function EZAssurePropertyView() {
  const { propertyId } = useParams();
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("monitoring");
  const [showDispute, setShowDispute] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const [showMonitor, setShowMonitor] = useState(false);
  const [disputeForm, setDisputeForm] = useState({ dispute_type: "title_challenge", description: "" });
  const [vaultForm, setVaultForm] = useState({ document_type: "deed", title: "", description: "" });
  const [monitorForm, setMonitorForm] = useState({ event_type: "mutation_change", description: "", severity: "medium" });
  const [generatingDraft, setGeneratingDraft] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ezassure/property/${propertyId}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
  }, [API, propertyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fileDispute = async () => {
    try {
      await fetch(`${API}/ezassure/disputes`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ property_id: propertyId, ...disputeForm }),
      });
      setShowDispute(false);
      setDisputeForm({ dispute_type: "title_challenge", description: "" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const addToVault = async () => {
    try {
      await fetch(`${API}/ezassure/vault/add`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ property_id: propertyId, ...vaultForm, file_data: btoa(vaultForm.title + " - " + vaultForm.description) }),
      });
      setShowVault(false);
      setVaultForm({ document_type: "deed", title: "", description: "" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const simulateEvent = async () => {
    try {
      await fetch(`${API}/ezassure/monitoring/simulate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ property_id: propertyId, ...monitorForm }),
      });
      setShowMonitor(false);
      setMonitorForm({ event_type: "mutation_change", description: "", severity: "medium" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const generateDraft = async (disputeId) => {
    setGeneratingDraft(disputeId);
    try {
      await fetch(`${API}/ezassure/disputes/${disputeId}/draft`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      });
      fetchData();
    } catch (e) { console.error(e); }
    setGeneratingDraft(null);
  };

  const assignLawyer = async (disputeId) => {
    try {
      await fetch(`${API}/ezassure/disputes/${disputeId}/assign-lawyer`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      });
      fetchData();
    } catch (e) { console.error(e); }
  };

  if (!data) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;

  const { subscription: sub, property: prop, monitoring_events: events, evidence_vault: vault, disputes, assure_status } = data;
  const st = STATUS_CONFIG[assure_status] || STATUS_CONFIG.unprotected;

  return (
    <div data-testid="ezassure-property-view">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button data-testid="back-to-ezassure" onClick={() => navigate("/dashboard/ezassure")} className="text-white/40 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="font-serif text-2xl text-white">{prop?.name}</h2>
            <span className="text-xs px-2 py-0.5 rounded-sm font-sans flex items-center gap-1" style={{ color: st.color, backgroundColor: st.color + "15", border: `1px solid ${st.color}40` }}>
              <st.icon className="w-3 h-3" /> {st.label}
            </span>
          </div>
          <p className="text-white/40 text-sm font-sans">{prop?.address}</p>
        </div>
      </div>

      {/* Coverage Summary */}
      {sub && (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans">Coverage — {sub.plan_name}</p>
            <span className="text-white/30 text-[10px] font-sans">{sub.monitoring_frequency} monitoring</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(sub.coverage || {}).map(([k, v]) => (
              <span key={k} className={`text-[10px] px-2 py-1 rounded-sm font-sans flex items-center gap-1 ${v ? "bg-[#556B2F]/10 text-[#556B2F] border border-[#556B2F]/20" : "bg-white/5 text-white/20 border border-white/5"}`}>
                {v ? <CheckCircle className="w-3 h-3" /> : <span className="w-3 h-3 text-center">-</span>}
                {k.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-[#11130F] border border-white/10 rounded-sm p-1 w-fit">
        {[
          { key: "monitoring", label: "Monitoring", count: events?.length },
          { key: "vault", label: "Evidence Vault", count: vault?.length },
          { key: "disputes", label: "Disputes", count: disputes?.length },
        ].map(t => (
          <button key={t.key} data-testid={`ezassure-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-xs font-sans rounded-sm transition-colors flex items-center gap-1.5 ${activeTab === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/50 hover:text-white"}`}>
            {t.label}
            {t.count > 0 && <span className="bg-white/10 text-white/40 text-[9px] px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Monitoring Tab */}
      {activeTab === "monitoring" && (
        <div>
          <div className="flex justify-end mb-3">
            <Button data-testid="simulate-event-btn" onClick={() => setShowMonitor(true)} variant="outline" className="border-white/10 text-white/50 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" /> Simulate Alert
            </Button>
          </div>
          {(!events || events.length === 0) ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-8 text-center">
              <ShieldCheck className="w-10 h-10 text-[#556B2F]/30 mx-auto mb-3" />
              <p className="text-white/40 text-sm font-sans">No monitoring alerts. Your property is being actively watched.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map(evt => (
                <div key={evt.event_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4 flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: SEVERITY_COLORS[evt.severity] || "#F59E0B" }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-sans text-xs font-medium capitalize">{evt.event_type.replace(/_/g, " ")}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-sans uppercase" style={{ color: SEVERITY_COLORS[evt.severity], backgroundColor: SEVERITY_COLORS[evt.severity] + "15" }}>{evt.severity}</span>
                    </div>
                    {evt.description && <p className="text-white/50 text-xs font-sans mt-1">{evt.description}</p>}
                    <p className="text-white/20 text-[10px] font-sans mt-1">{new Date(evt.detected_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Evidence Vault Tab */}
      {activeTab === "vault" && (
        <div>
          <div className="flex justify-end mb-3">
            <Button data-testid="add-vault-btn" onClick={() => setShowVault(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm text-xs">
              <Lock className="w-3 h-3 mr-1" /> Preserve Document
            </Button>
          </div>
          {(!vault || vault.length === 0) ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-8 text-center">
              <Lock className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/40 text-sm font-sans">Evidence vault is empty. Preserve important documents here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {vault.map(v => (
                <div key={v.vault_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center">
                      <Lock className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h4 className="text-white font-sans text-xs font-medium">{v.title}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[#D4AF37] text-[9px] font-sans uppercase">{v.document_type.replace(/_/g, " ")}</span>
                        <span className="text-white/20">|</span>
                        <span className="text-white/30 text-[10px] font-sans">Preserved {new Date(v.preserved_at).toLocaleDateString()}</span>
                      </div>
                      {v.description && <p className="text-white/40 text-[10px] font-sans mt-0.5">{v.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#556B2F] text-[9px] font-sans bg-[#556B2F]/10 px-2 py-0.5 rounded-sm flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5" /> Immutable
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Disputes Tab */}
      {activeTab === "disputes" && (
        <div>
          <div className="flex justify-end mb-3">
            <Button data-testid="file-dispute-btn" onClick={() => setShowDispute(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm text-xs">
              <Gavel className="w-3 h-3 mr-1" /> File Dispute
            </Button>
          </div>
          {(!disputes || disputes.length === 0) ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-8 text-center">
              <Gavel className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/40 text-sm font-sans">No disputes filed. eZAssure stands ready if needed.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {disputes.map(d => (
                <div key={d.dispute_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Gavel className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-white font-sans text-sm font-medium capitalize">{d.dispute_type.replace(/_/g, " ")}</span>
                      </div>
                      <p className="text-white/50 text-xs font-sans mt-1">{d.description}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans capitalize" style={{ color: DISPUTE_STATUS_COLORS[d.status] || "#F59E0B", backgroundColor: (DISPUTE_STATUS_COLORS[d.status] || "#F59E0B") + "15" }}>
                      {d.status.replace(/_/g, " ")}
                    </span>
                  </div>

                  {/* Timeline */}
                  {d.timeline?.length > 0 && (
                    <div className="mb-3 pl-4 border-l border-white/10 space-y-2">
                      {d.timeline.map((t, i) => (
                        <div key={i} className="text-[10px] font-sans">
                          <span className="text-white/60">{t.stage.replace(/_/g, " ")}</span>
                          <span className="text-white/20 ml-2">{new Date(t.at).toLocaleString()}</span>
                          {t.note && <span className="text-white/30 ml-2">— {t.note}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Draft */}
                  {d.draft_response && (
                    <div className="bg-white/5 border border-white/10 rounded-sm p-3 mb-3">
                      <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-sans mb-2">AI Legal Draft</p>
                      <p className="text-white/60 text-xs font-sans whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
                        {d.draft_response.replace(/\*\*(.*?)\*\*/g, '$1').replace(/###\s*/g, '').replace(/##\s*/g, '').replace(/#\s*/g, '')}
                      </p>
                    </div>
                  )}

                  {/* Assigned Lawyer */}
                  {d.assigned_lawyer && (
                    <div className="bg-[#556B2F]/10 border border-[#556B2F]/20 rounded-sm p-3 mb-3 flex items-center gap-3">
                      <Users className="w-4 h-4 text-[#556B2F]" />
                      <div>
                        <p className="text-white text-xs font-sans font-medium">{d.assigned_lawyer.name}</p>
                        <p className="text-white/40 text-[10px] font-sans">{d.assigned_lawyer.specialization}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    {!d.draft_response && (
                      <Button data-testid={`generate-draft-${d.dispute_id}`} onClick={() => generateDraft(d.dispute_id)} disabled={generatingDraft === d.dispute_id}
                        variant="outline" className="border-white/10 text-white/50 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm text-xs">
                        {generatingDraft === d.dispute_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                        Generate Draft
                      </Button>
                    )}
                    {!d.assigned_lawyer && (
                      <Button data-testid={`assign-lawyer-${d.dispute_id}`} onClick={() => assignLawyer(d.dispute_id)}
                        variant="outline" className="border-white/10 text-white/50 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm text-xs">
                        <Users className="w-3 h-3 mr-1" /> Assign Lawyer
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* File Dispute Dialog */}
      <Dialog open={showDispute} onOpenChange={setShowDispute}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">File Dispute</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Dispute Type</label>
              <Select value={disputeForm.dispute_type} onValueChange={v => setDisputeForm({ ...disputeForm, dispute_type: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="title_challenge">Title Challenge</SelectItem>
                  <SelectItem value="boundary_dispute">Boundary Dispute</SelectItem>
                  <SelectItem value="encroachment">Encroachment</SelectItem>
                  <SelectItem value="fraud_claim">Fraud Claim</SelectItem>
                  <SelectItem value="inheritance_dispute">Inheritance Dispute</SelectItem>
                  <SelectItem value="third_party_claim">Third Party Claim</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input data-testid="dispute-description" value={disputeForm.description} onChange={e => setDisputeForm({ ...disputeForm, description: e.target.value })} placeholder="Describe the dispute..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-dispute-btn" onClick={fileDispute} disabled={!disputeForm.description} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">File Dispute</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add to Vault Dialog */}
      <Dialog open={showVault} onOpenChange={setShowVault}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Preserve Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Document Type</label>
              <Select value={vaultForm.document_type} onValueChange={v => setVaultForm({ ...vaultForm, document_type: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="deed">Deed</SelectItem>
                  <SelectItem value="notice">Notice</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                  <SelectItem value="reply">Reply</SelectItem>
                  <SelectItem value="court_order">Court Order</SelectItem>
                  <SelectItem value="noc">NOC</SelectItem>
                  <SelectItem value="survey_report">Survey Report</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Title</label>
              <Input data-testid="vault-title" value={vaultForm.title} onChange={e => setVaultForm({ ...vaultForm, title: e.target.value })} placeholder="Document title..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input value={vaultForm.description} onChange={e => setVaultForm({ ...vaultForm, description: e.target.value })} placeholder="Details..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-vault-btn" onClick={addToVault} disabled={!vaultForm.title} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">Preserve in Vault</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Simulate Monitoring Dialog */}
      <Dialog open={showMonitor} onOpenChange={setShowMonitor}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Simulate Monitoring Alert</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Event Type</label>
              <Select value={monitorForm.event_type} onValueChange={v => setMonitorForm({ ...monitorForm, event_type: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="mutation_change">Mutation Change</SelectItem>
                  <SelectItem value="court_filing">Court Filing</SelectItem>
                  <SelectItem value="encroachment">Encroachment</SelectItem>
                  <SelectItem value="third_party_claim">Third Party Claim</SelectItem>
                  <SelectItem value="title_update">Title Update</SelectItem>
                  <SelectItem value="encumbrance_alert">Encumbrance Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Severity</label>
              <Select value={monitorForm.severity} onValueChange={v => setMonitorForm({ ...monitorForm, severity: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input value={monitorForm.description} onChange={e => setMonitorForm({ ...monitorForm, description: e.target.value })} placeholder="Alert details..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-monitor-btn" onClick={simulateEvent} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm">Simulate Alert</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
